import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Plus, Trash2, ArrowLeft } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import api from "@/lib/api";
import { ImageField } from "@/components/ImageField";
import { ListEditor } from "@/components/admin/ListEditor";

const emptyService = {
  slug: "", number: "", title: "", tagline: "", subtitle: "", hero_image: "",
  audience_label: "", short_pitch: "", cta_short: "", intro: "",
  highlights: [], doubts: [], included: [], excluded: [],
  timing: "", payment: "", cta: "", order: 0, published: true,
};

function F({ label, children, testId }) {
  return (
    <div data-testid={testId}>
      <Label className="eyebrow !tracking-[0.14em]">{label}</Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}

function DoubtsEditor({ items, onChange, testId }) {
  const update = (i, k, v) => onChange(items.map((d, j) => (j === i ? { ...d, [k]: v } : d)));
  return (
    <div className="space-y-4" data-testid={testId}>
      {items.map((d, i) => (
        <div key={i} className="border border-hair p-4 space-y-3 bg-bone/50">
          <div className="flex justify-between items-center gap-3">
            <Input value={d.title} onChange={(e) => update(i, "title", e.target.value)} placeholder="Titolo del dubbio" data-testid={`${testId}-title-${i}`} />
            <button type="button" onClick={() => onChange(items.filter((_, j) => j !== i))} className="text-red-700 hover:text-red-900 shrink-0" data-testid={`${testId}-remove-${i}`} aria-label="Rimuovi dubbio">
              <Trash2 size={15} />
            </button>
          </div>
          <Textarea rows={2} value={d.text} onChange={(e) => update(i, "text", e.target.value)} placeholder="Testo" data-testid={`${testId}-text-${i}`} />
        </div>
      ))}
      <button type="button" onClick={() => onChange([...items, { title: "", text: "" }])} data-testid={`${testId}-add`}
        className="min-h-[44px] inline-flex items-center gap-1 text-xs tracking-[0.14em] uppercase border border-ink px-3 py-2 hover:bg-ink hover:text-bone transition-colors">
        <Plus size={13} /> Aggiungi dubbio
      </button>
    </div>
  );
}

export function ServicesPanel() {
  const [services, setServices] = useState([]);
  const [editing, setEditing] = useState(null); // null = lista, object = form
  const [isNew, setIsNew] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/services").then((r) => setServices(r.data)).catch(() => toast.error("Errore caricamento servizi"));
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setEditing((p) => ({ ...p, [k]: e?.target ? e.target.value : e }));

  const save = async () => {
    if (!editing.title?.trim()) { toast.error("Il titolo è obbligatorio"); return; }
    setBusy(true);
    try {
      if (isNew) {
        await api.post("/admin/services", editing);
        toast.success("Servizio creato");
      } else {
        await api.put(`/admin/services/${editing.slug}`, editing);
        toast.success("Servizio aggiornato");
      }
      setEditing(null);
      await load();
    } catch (err) {
      const msg = err.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Errore salvataggio");
    } finally { setBusy(false); }
  };

  const remove = async (slug) => {
    if (!window.confirm("Eliminare definitivamente questo servizio?")) return;
    try {
      await api.delete(`/admin/services/${slug}`);
      toast.success("Servizio eliminato");
      setEditing(null);
      await load();
    } catch { toast.error("Errore eliminazione"); }
  };

  if (editing) {
    return (
      <div className="max-w-4xl" data-testid="service-editor">
        <div className="flex items-center justify-between flex-wrap gap-4 mb-8">
          <button onClick={() => setEditing(null)} className="inline-flex items-center gap-2 min-h-[44px] text-sm text-taupe hover:text-ink" data-testid="service-editor-back">
            <ArrowLeft size={15} /> Tutti i servizi
          </button>
          <div className="flex items-center gap-3 w-full sm:w-auto">
            {!isNew && (
              <button onClick={() => remove(editing.slug)} className="flex-1 sm:flex-none min-h-[44px] inline-flex items-center justify-center gap-2 text-xs tracking-[0.14em] uppercase border border-red-700 text-red-700 px-4 py-2 hover:bg-red-700 hover:text-bone transition-colors" data-testid="service-delete">
                <Trash2 size={13} /> Elimina
              </button>
            )}
            <button onClick={save} disabled={busy} className="btn-ink flex-1 sm:flex-none min-h-[44px] disabled:opacity-60" data-testid="service-save">
              {busy ? "Salvataggio…" : isNew ? "Crea servizio" : "Salva modifiche"}
            </button>
          </div>
        </div>

        <div className="bg-white border border-hair p-5 md:p-8 space-y-8">
          <div className="flex items-center justify-between gap-6 flex-wrap">
            <F label="Pubblicato sul sito" testId="service-field-published">
              <div className="flex items-center gap-3">
                <Switch checked={editing.published} onCheckedChange={(v) => setEditing((p) => ({ ...p, published: v }))} data-testid="service-published" />
                <span className="text-sm text-taupe">{editing.published ? "Visibile" : "Nascosto"}</span>
              </div>
            </F>
            <F label="Ordine" testId="service-field-order">
              <Input type="number" className="w-28" value={editing.order} onChange={(e) => setEditing((p) => ({ ...p, order: Number(e.target.value) }))} data-testid="service-order" />
            </F>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <F label="Titolo" testId="service-field-title">
              <Input value={editing.title} onChange={set("title")} data-testid="service-title" />
            </F>
            <F label="Slug (URL)" testId="service-field-slug">
              <Input value={editing.slug} onChange={set("slug")} disabled={!isNew} data-testid="service-slug" className="disabled:opacity-60" />
            </F>
            <F label="Numero (es. 01)" testId="service-field-number">
              <Input value={editing.number} onChange={set("number")} data-testid="service-number" />
            </F>
            <F label="Tagline" testId="service-field-tagline">
              <Input value={editing.tagline} onChange={set("tagline")} data-testid="service-tagline" />
            </F>
          </div>

          <F label="Sottotitolo" testId="service-field-subtitle">
            <Input value={editing.subtitle} onChange={set("subtitle")} data-testid="service-subtitle" />
          </F>

          <F label="Immagine di copertina" testId="service-field-hero">
            <ImageField value={editing.hero_image} onChange={(v) => setEditing((p) => ({ ...p, hero_image: v }))} testId="service-hero-image" />
          </F>

          <div className="grid md:grid-cols-2 gap-6">
            <F label="Etichetta pubblico" testId="service-field-audience">
              <Input value={editing.audience_label} onChange={set("audience_label")} data-testid="service-audience" />
            </F>
            <F label="CTA breve (card)" testId="service-field-cta-short">
              <Input value={editing.cta_short} onChange={set("cta_short")} data-testid="service-cta-short" />
            </F>
          </div>

          <F label="Pitch breve (card)" testId="service-field-pitch">
            <Textarea rows={2} value={editing.short_pitch} onChange={set("short_pitch")} data-testid="service-short-pitch" />
          </F>

          <F label="Introduzione (pagina dettaglio)" testId="service-field-intro">
            <Textarea rows={4} value={editing.intro} onChange={set("intro")} data-testid="service-intro" />
          </F>

          <F label="Punti chiave (highlights)" testId="service-field-highlights">
            <ListEditor items={editing.highlights} onChange={set("highlights")} placeholder="es. Da 990 €" testId="service-highlights" />
          </F>

          <F label="Dubbi che risolve" testId="service-field-doubts">
            <DoubtsEditor items={editing.doubts} onChange={set("doubts")} testId="service-doubts" />
          </F>

          <div className="grid md:grid-cols-2 gap-6">
            <F label="Cosa è incluso" testId="service-field-included">
              <ListEditor items={editing.included} onChange={set("included")} placeholder="Voce inclusa" testId="service-included" />
            </F>
            <F label="Cosa non è incluso" testId="service-field-excluded">
              <ListEditor items={editing.excluded} onChange={set("excluded")} placeholder="Voce esclusa" testId="service-excluded" />
            </F>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <F label="Tempistiche" testId="service-field-timing">
              <Textarea rows={2} value={editing.timing} onChange={set("timing")} data-testid="service-timing" />
            </F>
            <F label="Modalità di pagamento" testId="service-field-payment">
              <Textarea rows={2} value={editing.payment} onChange={set("payment")} data-testid="service-payment" />
            </F>
          </div>

          <F label="Testo CTA finale" testId="service-field-cta">
            <Input value={editing.cta} onChange={set("cta")} data-testid="service-cta" />
          </F>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="services-panel">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <p className="text-sm text-taupe">I percorsi mostrati nelle pagine Servizi e Home. Le modifiche sono subito online.</p>
        <button onClick={() => { setIsNew(true); setEditing({ ...emptyService, order: services.length }); }} className="btn-ink inline-flex items-center justify-center gap-2 w-full sm:w-auto min-h-[44px]" data-testid="service-new">
          <Plus size={15} /> Nuovo servizio
        </button>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {services.map((s) => (
          <button key={s.slug} onClick={() => { setIsNew(false); setEditing({ ...emptyService, ...s }); }} data-testid={`service-edit-${s.slug}`}
            className="text-left bg-white border border-hair p-6 hover:border-gold transition-colors group">
            <div className="flex justify-between items-start">
              <span className="font-serif text-3xl text-gold">{s.number}</span>
              <span className={`text-[10px] tracking-[0.14em] uppercase px-2 py-1 border ${s.published ? "border-gold/50 text-gold" : "border-hair text-taupe"}`}>
                {s.published ? "Online" : "Bozza"}
              </span>
            </div>
            <div className="mt-4 font-serif text-2xl">{s.title}</div>
            <div className="text-xs text-taupe mt-1">/servizi/{s.slug}</div>
            <div className="mt-4 inline-flex items-center gap-2 text-xs tracking-[0.14em] uppercase text-ink group-hover:text-gold transition-colors">
              <Pencil size={12} /> Modifica
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
